import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useLocation } from 'react-router-dom';

import { useMenu } from './useMenu';

import { useSidebar } from '../SidebarProvider/SidebarProvider';

import { Button } from 'components/ui/button';
import { useActions, useAppSelector } from 'features/hooks/useRedux';
import { selectAuth } from 'features/store/slices/auth';
import { Avatar, AvatarImage } from 'components/ui/avatar';
import { useLogout } from 'api/auth';
import { useTelegram } from 'features/hooks/useTelegram';
import { PlatformToggle } from 'components/common';
import { useIsMobile } from 'features/hooks/useMobile';

export const SidebarNavigation = () => {
  const location = useLocation();
  const { initData } = useTelegram();
  const { logoutUser } = useActions();
  const { isOpen, close } = useSidebar();

  const isMobileSidebar = useIsMobile(768);
  const isMobileMenu = useIsMobile(570);

  const [activePlatform, setActivePlatform] = useState<'telegram' | 'instagram'>(
    location.pathname?.startsWith('/instagram') ? 'instagram' : 'telegram',
  );

  const { user } = useAppSelector(selectAuth);

  const { mutateAsync: logout, isPending: isPendingLogout } = useLogout();

  const menu = useMenu(activePlatform, isMobileMenu);

  const handleLogout = async () => {
    try {
      await logout();
      logoutUser();
    } catch (error) {
      console.log(error);
    }
  };

  if (!isOpen || !user) return null;

  return (
    <div
      className={`${isMobileSidebar ? 'fixed inset-0 z-50 bg-black/50' : ''}`}
      onClick={isMobileSidebar ? close : undefined}
    >
      <aside
        className={`max-w-64 bg-gray-50 border-r h-screen ${isMobileSidebar ? 'fixed left-0 top-0 z-50' : 'sticky left-0 top-0'}`}
        onClick={(e) => e.stopPropagation()}
      >
        {isMobileSidebar && (
          <div className="absolute right-2 top-2">
            <Button variant="ghost" size="icon" onClick={close}>
              <X className="h-5 w-5" />
            </Button>
          </div>
        )}

        {user && (
          <div className="h-auto p-4 border-b flex items-center gap-2">
            {user?.photoUrl ? (
              <Avatar>
                <AvatarImage src={user?.photoUrl || ''} alt="@shadcn" />
              </Avatar>
            ) : (
              <div className="w-8 h-8 rounded-full bg-gray-200"></div>
            )}

            <div>
              <div className="font-medium">{user ? user.fullName : ''}</div>
              <div className="text-xs text-gray-500">{user?.role.name}</div>
            </div>
          </div>
        )}

        <div className="p-4 border-b">
          <PlatformToggle activePlatform={activePlatform} onChange={setActivePlatform} />
        </div>

        {menu}

        {!('user' in initData) && user && (
          <div className="absolute bottom-0 w-full p-2 h-[60px] border-t">
            <Button
              variant="outline"
              className="w-full justify-start text-red-500 hover:text-red-600 hover:bg-red-50"
              onClick={handleLogout}
              disabled={isPendingLogout}
            >
              Выйти
            </Button>
          </div>
        )}
      </aside>
    </div>
  );
};
